<template>
  <div class="table-content qaqc-consistency-check-wrap qaqc-equilibrium-plot-wrap">
    <div class="table-wrap">
      <div class="table-title-div">
        <p class="table-title">Equilibrium Plot Linearity Analysis</p>
        <div class="qaqc-scorecard-header-actions">
          <div class="csv-btn" @click="downloadTableCSV">
            <v-img class="pointer" :src="documentDownload" alt="csv download"></v-img>
          </div>
          <v-btn
            icon
            variant="text"
            size="small"
            class="qaqc-scorecard-collapse-btn"
            :aria-label="isCollapsed ? 'Expand' : 'Collapse'"
            @click="isCollapsed = !isCollapsed"
          >
            <v-icon>{{ isCollapsed ? 'mdi-chevron-down' : 'mdi-chevron-up' }}</v-icon>
          </v-btn>
        </div>
      </div>
      <div v-show="!isCollapsed" class="qaqc-consistency-check-body">
        <div class="qaqc-consistency-subsection">
          <p class="qaqc-consistency-desc qaqc-equilibrium-desc">
            Pass rates for Modified Wilson, Buckley, and Hoffman plot linearity across all samples. Click on non-compliant count to see affected samples.
          </p>
          <div v-if="rows.length" class="qaqc-consistency-table-wrap">
            <table class="qaqc-consistency-table">
              <thead>
                <tr class="qaqc-consistency-thead">
                  <th class="qaqc-consistency-th-property">Equilibrium Plot</th>
                  <th class="qaqc-consistency-th-num">Compliant</th>
                  <th class="qaqc-consistency-th-num">Non-Compliant</th>
                  <th class="qaqc-consistency-th-num">Compliance Rate</th>
                </tr>
              </thead>
              <tbody>
                <template v-for="(item, idx) in rowsWithChips" :key="'eq-' + idx">
                  <tr class="qaqc-consistency-tr">
                    <td class="qaqc-consistency-td-property">
                      <div class="qaqc-consistency-property-label">{{ item.row.displayName }}</div>
                    </td>
                    <td class="qaqc-consistency-td-num">{{ item.row.compliant }}</td>
                    <td
                      class="qaqc-consistency-td-num qaqc-consistency-nc-cell"
                      :class="{ 'qaqc-consistency-nc-cell--interactive': rowHasNonCompliant(item.row) }"
                      role="button"
                      :tabindex="rowHasNonCompliant(item.row) ? 0 : -1"
                      @click="rowHasNonCompliant(item.row) ? toggleExpand(idx) : null"
                      @keydown.enter.prevent="rowHasNonCompliant(item.row) ? toggleExpand(idx) : null"
                      @keydown.space.prevent="rowHasNonCompliant(item.row) ? toggleExpand(idx) : null"
                    >
                      <span class="qaqc-consistency-nc-inner">
                        <v-icon
                          v-if="rowHasNonCompliant(item.row)"
                          size="small"
                          class="qaqc-consistency-nc-chevron"
                        >{{ expandedIdx === idx ? 'mdi-chevron-up' : 'mdi-chevron-right' }}</v-icon>
                        <span class="qaqc-consistency-nc-value">{{ item.row.nonCompliant }}</span>
                      </span>
                    </td>
                    <td class="qaqc-consistency-td-num">{{ item.row.complianceRate }}</td>
                  </tr>
                  <tr
                    v-if="expandedIdx === idx && rowHasNonCompliant(item.row)"
                    class="qaqc-consistency-expand-row"
                  >
                    <td colspan="4" class="qaqc-consistency-expand-cell">
                      <div class="qaqc-consistency-expand-inner">
                        <span class="qaqc-consistency-sublabel">Non-compliant samples (click to view QC details)</span>
                        <div v-if="item.chips.length" class="qaqc-consistency-chips">
                          <span
                            v-for="(chip, cidx) in item.chips"
                            :key="chipDomKey(idx, chip, cidx)"
                            class="qaqc-consistency-chip qaqc-consistency-check-chip"
                            :class="chipClasses(idx, chip, cidx)"
                            role="button"
                            :tabindex="chipTabindex(idx, chip, cidx)"
                            :aria-busy="chipIsLoading(idx, chip, cidx)"
                            @click.stop="openSampleQc(chip, idx, cidx)"
                            @keydown.enter.prevent="activateChip(chip, idx, cidx)"
                            @keydown.space.prevent="activateChip(chip, idx, cidx)"
                          >
                            <v-progress-circular
                              v-if="chipIsLoading(idx, chip, cidx)"
                              indeterminate
                              size="18"
                              width="2"
                              color="primary"
                              class="qaqc-consistency-check-chip-loader"
                            />
                            <template v-else>{{ chipLabel(chip) }}</template>
                          </span>
                        </div>
                        <div v-else class="qaqc-consistency-no-ids">No sample IDs returned for this check.</div>
                      </div>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
          <div v-else class="qaqc-equilibrium-empty">No equilibrium plot linearity data available.</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from "@/axiosInterceptor";
import { getTenantPrefix } from "@/utils/tenant";

export default {
  name: "EquilibriumPlotLinearitySection",
  props: {
    rows: { type: Array, default: () => [] },
  },
  data() {
    return {
      documentDownload: require("@/assets/Images/csv.svg"),
      isCollapsed: false,
      expandedIdx: null,
      loadingChipKey: null,
    };
  },
  computed: {
    rowsWithChips() {
      return (this.rows || []).map((row) => ({
        row,
        chips: this.buildChips(row),
      }));
    },
  },
  watch: {
    rows() {
      this.expandedIdx = null;
      this.loadingChipKey = null;
    },
  },
  methods: {
    nonCompliantCountNum(row) {
      const raw = row?.nonCompliant;
      if (raw === undefined || raw === null || raw === "-") return 0;
      const n = Number(String(raw).replace(/[^0-9.-]/g, ""));
      return Number.isNaN(n) ? 0 : n;
    },
    rowHasNonCompliant(row) {
      return this.nonCompliantCountNum(row) > 0;
    },
    toggleExpand(idx) {
      const item = this.rowsWithChips[idx];
      if (!item || !this.rowHasNonCompliant(item.row)) return;
      this.expandedIdx = this.expandedIdx === idx ? null : idx;
    },
    buildChips(row) {
      const fs = row?.failedSamples;
      if (Array.isArray(fs) && fs.length) return fs;
      return (row?.fluidSampleIds || []).map((fid) => ({ fluidSampleID: String(fid), id: "" }));
    },
    chipInstanceKey(rowIdx, chip, chipIdx) {
      return `eq:r${rowIdx}:i${chipIdx}:${String(chip.id ?? "")}:f${String(chip.fluidSampleID ?? "")}`;
    },
    chipDomKey(rowIdx, chip, chipIdx) {
      return this.chipInstanceKey(rowIdx, chip, chipIdx);
    },
    chipIsLoading(rowIdx, chip, chipIdx) {
      return this.loadingChipKey === this.chipInstanceKey(rowIdx, chip, chipIdx);
    },
    chipIsBusyOther(rowIdx, chip, chipIdx) {
      return this.loadingChipKey != null && !this.chipIsLoading(rowIdx, chip, chipIdx);
    },
    chipClasses(rowIdx, chip, chipIdx) {
      return {
        "qaqc-consistency-check-chip--loading": this.chipIsLoading(rowIdx, chip, chipIdx),
        "qaqc-consistency-check-chip--disabled": this.chipIsBusyOther(rowIdx, chip, chipIdx),
      };
    },
    chipTabindex(rowIdx, chip, chipIdx) {
      if (this.chipIsLoading(rowIdx, chip, chipIdx)) return -1;
      if (this.chipIsBusyOther(rowIdx, chip, chipIdx)) return -1;
      return 0;
    },
    chipLabel(chip) {
      return chip.fluidSampleID || chip.id || "";
    },
    resolveTenantId() {
      return localStorage.getItem("tenantId") || getTenantPrefix();
    },
    pushQaqcUnifiedTestRoute(routeTestType, testId) {
      const tenantId = this.resolveTenantId();
      const tid = testId != null ? String(testId) : "";
      if (!tenantId || !routeTestType || !tid) return;
      this.$router.push(`/${tenantId}/qaqc/test/${routeTestType}/${tid}`);
    },
    samplePayloadFromViewSummary(res) {
      const d = res?.data?.data;
      if (!d) return null;
      if (d.sample && typeof d.sample === "object") return d.sample;
      const list = d.samples;
      if (Array.isArray(list) && list[0]) return list[0];
      return null;
    },
    async fetchSamplePayloadForChip(chip) {
      if (chip.id) {
        const res = await api.get(`public/api/samples/${chip.id}?viewSummary=true`);
        return this.samplePayloadFromViewSummary(res);
      }
      const fid = chip.fluidSampleID;
      if (!fid) return null;
      const res = await api.get("public/api/samples", {
        params: {
          fluidSampleID: `["${String(fid)}"]`,
          viewSummary: true,
          pageNumber: 1,
          pageSize: 10,
        },
      });
      const samples = res?.data?.data?.samples;
      return Array.isArray(samples) && samples[0] ? samples[0] : null;
    },
    activateChip(chip, rowIdx, chipIdx) {
      if (this.loadingChipKey) return;
      this.openSampleQc(chip, rowIdx, chipIdx);
    },
    async openSampleQc(chip, rowIdx, chipIdx) {
      const key = this.chipInstanceKey(rowIdx, chip, chipIdx);
      if (!key || this.loadingChipKey) return;
      this.loadingChipKey = key;
      try {
        const s = await this.fetchSamplePayloadForChip(chip);
        if (!s) return;
        const mongoId = s.ID || s._id || s.id;
        if (!mongoId) return;

        let testId = null;
        let routeTestType = null;
        if (s.dltests?.length) {
          const t = s.dltests[0];
          testId = t.ID || t._id || t.id;
          routeTestType = "dltest";
        }
        if (!testId && s.ccetests?.length) {
          const t = s.ccetests[0];
          testId = t.ID || t._id || t.id;
          routeTestType = "ccetest";
        }

        localStorage.setItem("sampleId", String(mongoId));
        if (testId && routeTestType) {
          localStorage.setItem("testId", testId);
          window.dispatchEvent(new CustomEvent("sampleIdChanged"));
          this.pushQaqcUnifiedTestRoute(routeTestType, testId);
        } else {
          const well = s.location?.well ?? s.wellName ?? s.well;
          if (well) {
            this.$router.push({ name: "QAQCDetailView", query: { well: String(well) } });
          } else {
            this.$router.push({ name: "QAQCDetailView" });
          }
        }
      } catch {
        // Network or API error
      } finally {
        this.loadingChipKey = null;
      }
    },
    downloadTableCSV() {
      if (!this.rows.length) return;
      const fileName = "equilibrium_plot_linearity_analysis.csv";
      const csvHeader = ["Equilibrium Plot", "Compliant", "Non-Compliant", "Compliance Rate", "Sample IDs"];
      const csvRows = this.rows.map((row) => [
        String(row.displayName ?? ""),
        String(row.compliant ?? ""),
        String(row.nonCompliant ?? ""),
        String(row.complianceRate ?? ""),
        (row.fluidSampleIds || []).join("; "),
      ]);
      const csvContent = [csvHeader.join(","), ...csvRows.map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))].join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    },
  },
};
</script>

<style scoped>
.qaqc-equilibrium-desc {
  margin: 0 0 12px;
  font-size: 13px;
  color: #4b5563;
}

.qaqc-equilibrium-empty {
  font-size: 13px;
  color: #6b7280;
  padding: 8px 0;
}

.qaqc-consistency-nc-cell--interactive {
  cursor: pointer;
}

.qaqc-consistency-nc-inner {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  color: #f44336;
  font-weight: 500;
}

.qaqc-consistency-nc-chevron {
  color: #f44336 !important;
}

.qaqc-consistency-expand-row {
  background: #fafafa;
}

.qaqc-consistency-expand-cell {
  padding: 12px 16px !important;
  vertical-align: top;
}

.qaqc-consistency-expand-inner {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.qaqc-consistency-no-ids {
  font-size: 12px;
  color: #6b7280;
}

.qaqc-consistency-check-chip {
  background: #FEE2E2;
  border: 1px solid #EF4444;
  color: #EF4444;
}

.qaqc-consistency-check-chip:hover:not(.qaqc-consistency-check-chip--loading):not(.qaqc-consistency-check-chip--disabled) {
  background: #fecaca;
}

.qaqc-consistency-check-chip--loading {
  cursor: wait;
  pointer-events: none;
  min-width: 40px;
  min-height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.qaqc-consistency-check-chip--disabled {
  cursor: not-allowed;
  pointer-events: none;
  opacity: 0.45;
}

.qaqc-consistency-check-chip-loader {
  flex-shrink: 0;
}
</style>
